// alter-column edit planner (v2 Slice A1).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice A1.
//
// Like v1's column ALTER ops, alter-column appends a fresh statement after
// the source statement (DSE2-7: no coalescing by default; opt-in coalescing
// is slice H1). Some engines emit a sequence of statements joined by `;\n`
// — mssql defaults are named CONSTRAINT objects so a default change is two
// statements (DROP + ADD); pgsql emits one ALTER per changed field.
//
// Supports both ALTER TABLE and CREATE TABLE source paths — the latter so
// callers that built up structure on a CREATE TABLE statement can still emit
// a follow-up ALTER without re-extracting from the table itself.

import { rangeFromOffsets } from '../../../sql-grammar/document/lineIndex';
import type { QualifiedName } from '../../../sql-grammar/model';
import type { DdlEdit, DdlStructure, PlannedByteEdit } from '../../types';

import { pickAppendInsertion } from './appendInsertion';
import type { DdlEditCtx } from './applyDdlEdit';
import { sourceSpellingOrigin } from './columnEdit.alterTable';
import { synthesizeAlterColumn } from './dialect.alterColumn';

type AlterColumnEdit = Extract<DdlEdit, { op: 'alter-column' }>;

export function planAlterColumnEdit(
  ctx: DdlEditCtx,
  edit: AlterColumnEdit,
): readonly PlannedByteEdit[] {
  const table = pickTable(ctx.structure);
  if (!table) {
    throw new Error(
      `applyDdlEdit: cannot apply alter-column edit to ${ctx.structure.kind} statement`,
    );
  }
  const synth = synthesizeAlterColumn(
    ctx.engine,
    table,
    edit.columnName,
    edit.change,
    {
      quoteName: table.quoted,
      // The host's quoting is a STYLE; it may not decide which column the
      // emitted ALTER names. `columnEdit.alterTable.ts` already reads that
      // answer off the source's own clauses for DROP / RENAME COLUMN — an
      // ALTER COLUMN names an existing column under the identical contract,
      // and a CREATE TABLE source answers from its column list the same way.
      //
      // Not free text: the Visual DDL editor's per-row Edit affordance passes
      // `columnName = row.name` straight off this structure
      // (`render/table/ColumnsTab.tsx` → `columnRows.ts`), so under a
      // consistently-quoted `CREATE TABLE "t" ("Id" integer)` the name is
      // exactly the bytes the source declared.
      //
      // That NAME is still not the row's identity when the source declares
      // two columns with the same bytes and different delimiters, so the
      // payload may carry the selected occurrence's own origin; without it a
      // source that declares both spellings REFUSES rather than guessing
      // (`sourceSpellingOrigin`).
      columnNameOrigin: edit.columnNameOrigin
        ?? sourceSpellingOrigin(ctx.structure, edit.columnName),
      mssqlExistingDefaultConstraintName: edit.mssqlExistingDefaultConstraintName,
      mssqlNewDefaultConstraintName: edit.mssqlNewDefaultConstraintName,
    },
  );
  const { offset: insertAt, sep, trailSep } = pickAppendInsertion(
    ctx.source,
    ctx.statementInfo.range.endOffset,
  );
  return [{
    range: rangeFromOffsets(ctx.lineIdx, insertAt, insertAt),
    replacement: sep + synth + trailSep,
    rationale: 'alter-column:alter-table',
  }];
}

function pickTable(s: DdlStructure): QualifiedName | undefined {
  if (s.kind === 'alter-table') return s.table;
  if (s.kind === 'create-table') return s.table;
  return undefined;
}
